import PropTypes from 'prop-types';
import { forwardRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Avatar, Chip, ListItemButton, ListItemIcon, ListItemText, Typography, Box } from '@mui/material';

// third party libraries
import { useTranslation } from 'react-i18next';

// project import
import { activeCollapseItem, activeItem } from 'store/reducers/menu';
import SvgIcon from 'components/@ui/icon/SvgIcon';

// ==============================|| NAVIGATION - LIST ITEM ||============================== //

const NavItem = ({ item, level }) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const { t } = useTranslation();

  const { drawerOpen, openItem } = useSelector((state) => state.menu);

  let itemTarget = '_self';
  if (item.target) {
    itemTarget = '_blank';
  }

  let listItemProps = { component: forwardRef((props, ref) => <Link ref={ref} {...props} to={item.url} target={itemTarget} />) };
  if (item?.external) {
    listItemProps = { component: 'a', href: item.url, target: itemTarget };
  }

  const itemHandler = (id) => {
    dispatch(activeItem({ openItem: [id] }));
    if (!item.parent_id) {
      dispatch(activeCollapseItem({ openCollapseItem: [''] }));
    }
  };

  const Icon = item.icon;
  const itemIcon = item.icon ? <Icon style={{ fontSize: drawerOpen ? '1.25rem' : '1.25rem' }} /> : false;
  // const itemIcon = item.icon ? (
  //   <SvgIcon src="/assets/images/icons/menu-icons/item-icon.svg" alt="Menu Icon" sx={{ fontSize: drawerOpen ? '1.25rem' : '1.25rem' }} />
  // ) : (
  //   false
  // );

  const isSelected = openItem.findIndex((id) => id === item.id) > -1;
  // active menu item on page load
  useEffect(() => {
    if (pathname.includes(item.url)) {
      dispatch(activeItem({ openItem: [item.id] }));
    }

    // eslint-disable-next-line
  }, [dispatch, pathname]);

  const textColor = 'text.primary';
  // const iconSelectedColor = 'primary.main';
  const iconSelectedColor = theme.palette.primary[400];

  if (!drawerOpen && level !== 1) {
    return null;
  }

  return (
    <ListItemButton
      {...listItemProps}
      disabled={item.disabled}
      onClick={() => itemHandler(item.id)}
      selected={isSelected}
      sx={{
        zIndex: 1201,
        pl: drawerOpen ? `${level * 28}px` : 1.5,
        py: !drawerOpen && level === 1 ? 1.25 : 1.25,
        ...(drawerOpen && {
          '&:hover': {
            bgcolor: 'secondary.light'
          },
          '&.Mui-selected': {
            bgcolor: 'secondary.lighter',
            // backgroundImage: 'linear-gradient(45deg, #08B496 30%, #00B1E7 90%)!important',
            borderRight: `2px solid`,
            borderColor: iconSelectedColor,
            // borderImageSource: 'linear-gradient(45deg, #08B496 30%, #00B1E7 90%)!important',
            botderImageSlice: 1,
            color: iconSelectedColor,
            '&:hover': {
              color: iconSelectedColor,
              bgcolor: 'secondary.light'
            }
          }
        }),
        ...(!drawerOpen && {
          '&:hover': {
            bgcolor: 'transparent'
          },
          '&.Mui-selected': {
            '&:hover': {
              bgcolor: 'transparent'
            },
            bgcolor: 'transparent'
          }
        })
      }}
    >
      {itemIcon && (
        <ListItemIcon
          sx={{
            minWidth: 28,
            color: isSelected ? iconSelectedColor : textColor,
            ...(!drawerOpen && {
              borderRadius: 1.5,
              width: 28,
              height: 28,
              alignItems: 'center',
              justifyContent: 'center',
              '&:hover': {
                bgcolor: 'secondary.lighter'
              }
            }),
            ...(!drawerOpen &&
              isSelected && {
                bgcolor: 'secondary.lighter',
                '&:hover': {
                  bgcolor: 'secondary.light'
                }
              })
          }}
        >
          {itemIcon}
        </ListItemIcon>
      )}
      {(drawerOpen || (!drawerOpen && level !== 1)) && (
        <ListItemText
          primary={
            <Typography
              variant="h6"
              sx={{ color: isSelected ? iconSelectedColor : textColor, fontSize: '0.80rem', lineHeight: '1.563', fontWeight: 500 }}
            >
              {t(item.title)}
            </Typography>
          }
        />
      )}
      {(drawerOpen || (!drawerOpen && level !== 1)) && item.chip && (
        <Chip
          color={item.chip.color}
          variant={item.chip.variant}
          size={item.chip.size}
          label={item.chip.label}
          avatar={item.chip.avatar && <Avatar>{item.chip.avatar}</Avatar>}
        />
      )}
    </ListItemButton>
  );
};

NavItem.propTypes = {
  item: PropTypes.object,
  level: PropTypes.number
};

export default NavItem;
